import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Avatar,
  Typography,
  IconButton,
  TableSortLabel,
  Breadcrumbs,
  useTheme,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import {
  PersonAdd,
  Edit,
  Delete,
  Close,
  Person,
  Check,
  Cancel,
  Email // ADDED for send email button
} from '@mui/icons-material';
import axios from '../utils/axiosInstance';
import Layout from './Layout';
import { useAuth } from '../utils/AuthContext';

// --- Constants for Dropdowns ---
const ROLES = process.env.REACT_APP_USER_ROLES ? process.env.REACT_APP_USER_ROLES.split(',') : ['admin', 'manager', 'team_member', 'viewer', 'file_limited'];
const MANUFACTURERS = process.env.REACT_APP_CLIENTS ? process.env.REACT_APP_CLIENTS.split(',') : ['ciita', 'almatica'];

const AdminPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    role: ROLES[ROLES.length - 1],
    manufacturer: '',
  });
  const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [openSendEmailConfirmModal, setOpenSendEmailConfirmModal] = useState(false); // ADDED
  const [userToEmail, setUserToEmail] = useState(null); // ADDED
  const [columns, setColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'username', direction: 'asc' });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // ADDED for success messages
  const [sendingEmail, setSendingEmail] = useState(null); // ADDED to track which user's email is being sent

  const fetchUsers = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get('/admin/users', { withCredentials: true });
      setUsers(response.data);

      if (response.data.length > 0) {
        const firstUser = response.data[0];
        const availableColumns = Object.keys(firstUser).filter(
          (key) => key !== 'id' && key !== '__v'
        );
        setColumns(availableColumns);
      }
    } catch (error) {
      setError('Failed to fetch users');
    }
  }, [user]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user) => {
    setEditMode(true);
    setCurrentUser({ ...user, password: '' }); 
    setError(null);
  };

  const handleSave = async () => {
    try {
      const updatedUser = { ...currentUser };
      if (updatedUser.password === '') {
        delete updatedUser.password;
      }

      const response = await axios.put(`/admin/users/${currentUser.id}`, updatedUser, {
        withCredentials: true,
      });

      if (response.status === 200) {
        await fetchUsers(); 
        setEditMode(false);
        setCurrentUser(null);
        setError(null);
        setSuccess('User updated successfully');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.msg || 'Failed to update user';
      setError(errorMessage);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.role) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const registrationData = {
          username: newUser.username,
          email: newUser.email,
          role: newUser.role,
          manufacturer: newUser.manufacturer,
      };
      const response = await axios.post('/auth/register', registrationData, {
        withCredentials: true,
      });

      if (response.status === 201) {
        await fetchUsers(); 
        setOpenAddUserModal(false);
        setNewUser({ username: '', email: '', password: '', role: ROLES[ROLES.length - 1], manufacturer: '' });
        setError(null);
        setSuccess('User created successfully. You can now send them a setup email.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.msg || 'Failed to create user';
      setError(errorMessage);
    }
  };

  // ADDED: Function to send setup email
  const handleSendSetupEmail = async () => {
    if (!userToEmail) return;
    
    setSendingEmail(userToEmail.id);
    try {
      const response = await axios.post(`/admin/send-setup-email/${userToEmail.id}`, {}, {
        withCredentials: true,
      });

      if (response.status === 200) {
        setSuccess(`Setup email sent successfully to ${userToEmail.username}`);
        setOpenSendEmailConfirmModal(false);
        setUserToEmail(null);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.msg || 'Failed to send setup email';
      setError(errorMessage);
    } finally {
      setSendingEmail(null);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await axios.delete(`/admin/users/${userToDelete.id}`, {
        withCredentials: true,
      });

      if (response.status === 200) {
        await fetchUsers(); 
        setOpenDeleteConfirmModal(false);
        setUserToDelete(null);
        setError(null);
        setSuccess('User deleted successfully');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.msg || 'Failed to delete user';
      setError(errorMessage);
      
      if (error.response?.status === 400 && error.response.data.msg === "Cannot delete yourself") {
        setOpenDeleteConfirmModal(false);
      }
    }
  };

  const handleChange = (e, field) => {
    const value = e.target.value;
    if (editMode) {
      setCurrentUser(prev => ({ ...prev, [field]: value }));
    } else {
      setNewUser(prev => ({ ...prev, [field]: value }));
    }
    setError(null);
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderCellContent = (user, column) => {
    const isEditing = editMode && currentUser?.id === user.id;

    if (isEditing) {
      if (column === 'role') {
        return (
          <FormControl fullWidth size="small" variant="standard">
            <Select
              value={currentUser.role}
              onChange={(e) => handleChange(e, 'role')}
            >
              {ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      if (column === 'manufacturer') {
        return (
          <FormControl fullWidth size="small" variant="standard">
            <Select
              value={currentUser.manufacturer || ''}
              onChange={(e) => handleChange(e, 'manufacturer')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {MANUFACTURERS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      return (
        <TextField
          value={currentUser[column] || ''}
          onChange={(e) => handleChange(e, column)}
          fullWidth
          size="small"
          variant="standard"
          type={column === 'password' ? 'password' : 'text'} 
          placeholder={column === 'password' ? 'Enter new password or leave blank' : ''}
        />
      );
    }
    
    return user[column];
  };

  if (!user || user.role !== 'admin') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Unauthorized Access
        </Typography>
      </Box>
    );
  }

  return (
    <Layout>
      <Box sx={{ 
        p: 3, 
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setError(null)} 
            severity="error" 
            sx={{ width: '100%', boxShadow: 3 }}
          >
            {error}
          </Alert>
        </Snackbar>

        {/* ADDED: Success Snackbar */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSuccess(null)} 
            severity="success" 
            sx={{ width: '100%', boxShadow: 3 }}
          >
            {success}
          </Alert>
        </Snackbar>

        {/* Header Section */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Typography color="text.primary">
              User Management
            </Typography>
          </Breadcrumbs>
          
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setOpenAddUserModal(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1
            }}
          >
            Add New User
          </Button>
        </Box>

        {/* Users Table */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            flex: 1,
            overflow: 'auto',
            minWidth: 1000,
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
              py: 1.5
            }
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column} sx={{ bgcolor: theme.palette.background.default }}>
                    <TableSortLabel
                      active={sortConfig.key === column}
                      direction={sortConfig.direction}
                      onClick={() => handleSort(column)}
                    >
                      {column.split('_').map(word => word[0].toUpperCase() + word.slice(1)).join(' ')}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ bgcolor: theme.palette.background.default }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.id} hover>
                  {columns.map((column) => (
                    <TableCell key={`${user.id}-${column}`}>
                      {renderCellContent(user, column)}
                    </TableCell>
                  ))}
                  <TableCell>
                    {editMode && currentUser?.id === user.id ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton onClick={handleSave} color="success" size="small">
                          <Check />
                        </IconButton>
                        <IconButton onClick={() => setEditMode(false)} color="error" size="small">
                          <Cancel />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* ADDED: Send Email Button */}
                        <IconButton 
                          onClick={() => {
                            setUserToEmail(user);
                            setOpenSendEmailConfirmModal(true);
                          }}
                          color="info"
                          size="small"
                          disabled={sendingEmail === user.id}
                          title="Send setup email"
                        >
                          <Email />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleEdit(user)} 
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setUserToDelete(user);
                            setOpenDeleteConfirmModal(true);
                          }}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add User Dialog */}
        <Dialog 
          open={openAddUserModal} 
          onClose={() => setOpenAddUserModal(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { minWidth: 500 } }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 2,
            pr: 6
          }}>
            <PersonAdd sx={{ mr: 1.5, color: theme.palette.primary.main }} />
            <Typography variant="h6">Create New User</Typography>
            <IconButton 
              onClick={() => setOpenAddUserModal(false)} 
              sx={{ ml: 'auto', color: theme.palette.text.secondary }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 3, '& .MuiTextField-root': { my: 1.5 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                label="Username *"
                value={newUser.username}
                onChange={(e) => handleChange(e, 'username')}
                fullWidth
                variant="outlined"
                size="small"
                required
              />
              <TextField
                label="Email *"
                value={newUser.email}
                onChange={(e) => handleChange(e, 'email')}
                fullWidth
                variant="outlined"
                size="small"
                required
              />

              <FormControl fullWidth variant="outlined" size="small" required>
                <InputLabel id="role-select-label">Role *</InputLabel>
                <Select
                  labelId="role-select-label"
                  id="role-select"
                  value={newUser.role}
                  label="Role *"
                  onChange={(e) => handleChange(e, 'role')}
                >
                  {ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {columns.includes('manufacturer') && (
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="manufacturer-select-label">Manufacturer</InputLabel>
                  <Select
                    labelId="manufacturer-select-label"
                    id="manufacturer-select"
                    value={newUser.manufacturer}
                    label="Manufacturer"
                    onChange={(e) => handleChange(e, 'manufacturer')}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {MANUFACTURERS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ 
            px: 3, 
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`
          }}>
            <Button 
              onClick={() => setOpenAddUserModal(false)}
              sx={{ color: theme.palette.text.secondary }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddUser}
              sx={{ borderRadius: 2 }}
            >
              Create User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={openDeleteConfirmModal} 
          onClose={() => setOpenDeleteConfirmModal(false)}
          sx={{
            '& .MuiDialog-paper': {
              minWidth: 400
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            Confirm User Deletion
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.error.light }}>
                <Person />
              </Avatar>
              <Typography>
                Are you sure you want to delete user <strong>{userToDelete?.username}</strong>?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenDeleteConfirmModal(false)}
              sx={{ color: theme.palette.text.secondary }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteUser}
              color="error"
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* ADDED: Send Email Confirmation Dialog */}
        <Dialog 
          open={openSendEmailConfirmModal} 
          onClose={() => setOpenSendEmailConfirmModal(false)}
          sx={{
            '& .MuiDialog-paper': {
              minWidth: 400
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            Confirm Send Setup Email
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.info.light }}>
                <Email />
              </Avatar>
              <Box>
                <Typography>
                  Send account setup email to <strong>{userToEmail?.username}</strong>?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Email: {userToEmail?.email}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenSendEmailConfirmModal(false)}
              sx={{ color: theme.palette.text.secondary }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendSetupEmail}
              color="info"
              variant="contained"
              sx={{ borderRadius: 2 }}
              disabled={sendingEmail === userToEmail?.id}
            >
              {sendingEmail === userToEmail?.id ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default AdminPage;